/**
 * RowEditDialog: the record page's add and edit form for one row of a
 * projection (the direct-edit routes).
 *
 * The form is REGISTRY-DERIVED: its inputs come from the ONE payload
 * registry (@/lib/noteVocabulary directFormFields), rendered by kind:
 * enum → SelectField, or SegmentedToggle for a two-value enum; date →
 * date input; boolean → checkbox; risk_category → the platform list plus
 * the current value; enum_set → by its RECORD_TICK_GROUPS arrangement,
 * the code-status ticks or one wrapping row of ticks (TickGroup, a
 * medication's When); string → a text
 * box, or a growing box of many lines when the field is multiline.
 * Never a hand list of fields. date_text is display-only: never a form
 * input, but its value rides the payload-only resubmit below.
 *
 * The words come from @/lib/recordFormWords: the title names the kind of
 * thing ("Add condition", "Edit medication"), the primary names the
 * outcome ("Add condition", "Save medication"), labels and help lines
 * are the card's, every select opens on a real answer (a registry
 * default, a prompt, or the empty state), and stored tokens read in
 * plain words. A caller may pass its own title, primary words, an intro
 * paragraph, and the subset of fields to draw.
 *
 * The primary is pressable at rest and disabled only while the request
 * is in flight. Pressed:
 *   - with a gap, it sends nothing, shows each gap's sentence under its
 *     own box and moves focus to the first gap. A gap is a required
 *     field that is empty, a field another field's value requires that
 *     is empty (an appointment's date, with its status), a changed
 *     phone with a problem, a date past by nature that is later than
 *     the practice's today, or a required toggle with neither side on;
 *   - in edit mode with nothing changed, it closes and sends nothing;
 *   - otherwise it sends and, on success, invalidates the ['clientRecord']
 *     aggregate (one audited re-read; a register natural-key change may
 *     re-land on a different row id, so the echo is never patched into
 *     the cache).
 *
 * What travels. An add sends every applying field that holds a value,
 * drawn or not: a preset passed through `values` and a default the
 * dialog opened on included. An edit sends the changed fields, `null`
 * for an optional box that held a value and is now empty (isClearable,
 * a clearable set of ticks included), ['unknown'] for an emptied
 * code-status set, nothing for any other emptied set, and the
 * projection's payload-only riders (RECORD_EDIT_PROJECTIONS) so they
 * survive the source-event re-point. A field that does not apply to the
 * values as they stand (the registry's appliesWhen, or a browser rule:
 * Named person, a medication's Ended) is not drawn, not counted and not
 * sent, with one exception: under a rule with clearWhenHidden, a stored
 * value that was drawn when the dialog opened and is hidden at save is
 * sent as null (a medication moved back to Active loses its end date).
 *
 * A field named in @/lib/recordDirectoryFields draws the
 * DirectoryNameBox on an add: its text is the value, a pick adds the
 * Directory link beside it, and a keystroke drops the link, so a link
 * never travels without its name. When the server refuses the link (the
 * entry is gone), the alert line says so and the link is dropped, so
 * the next press saves the name as typed.
 *
 * A field named in RECORD_CARE_TEAM_FIELDS (a medication's prescriber)
 * draws the CareTeamCombobox on an add and an edit: the client's care
 * team, from the record already in the query cache, then the Directory.
 * Its text is the value. An edit opens on the row's linked care-team row
 * as its pick. A pick that differs from the one the dialog opened on
 * travels beside the text as exactly one id: the care-team row, or the
 * Directory person or organization. An unchanged pick sends no id, and a
 * keystroke drops the pick, so an emptied box sends only the cleared
 * name.
 *
 * A save's failure is one sentence on the alert line above the buttons,
 * in this dialog's own words for the field (recordEditErrorMessage).
 *
 * Add insurance carries the "From a photo" door (CoverageCardPhoto) for a
 * seat that keeps the record and uses the AI tools, while the tenant's
 * photo reader is on. A read fills a box only when it is empty or still
 * holds what an earlier read put there, through the box's own transform
 * (a phone formatted, a name in its name case, a code as read); a box the
 * person typed in is kept. With "Keep the photos in Documents" on, the
 * photos read are filed as insurance documents after Add insurance
 * succeeds, never before; a filing failure after the save says so and
 * leaves only Close.
 */

import { useSupportContact } from '@/hooks/useSupportContact';
import { TextAction } from '@/components/workspace/TextAction';
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canSetBillTo, canUseAiTools, canWriteClinicalRecord } from '@/lib/capabilities';
import { usePhotoIntakeDoor } from '@/lib/aiToolToggles';
import { clientDocumentsKey, documentsApi } from '@/lib/documents';
import { CARD_TEXT_FIELDS, intakeImageFile, type InsuranceCardRead, type IntakeImage } from '@/lib/photoIntakeApi';
import { CoverageCardPhoto, type CardReadOutcome } from '@/components/clientRecord/CoverageCardPhoto';
import { ErrorNotice } from '@/components/ErrorNotice';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cachedClientRecord } from '@/lib/clientRecordApi';
import { api } from '@/lib/api';
import type { ClientPronouns } from '@/lib/clientRecord';
import {
  CODE_STATUS_COMBINABLE,
  DEFAULT_RISK_CATEGORIES,
  PROPOSAL_PAYLOAD_SPECS,
  directFormFields,
  editableFields,
  fieldApplies,
  isClearable,
  type PayloadFieldSpec,
} from '@/lib/noteVocabulary';
import {
  RECORD_ADD_PROJECTIONS,
  patchRecordRow,
  postRecordRow,
  recordEditErrorMessage,
  type RecordAddProjection,
  type RecordEditErrorContext,
  type RecordEditProjection,
} from '@/lib/clientRecordEditApi';
import {
  CODE_STATUS_GROUP_LINE,
  RECORD_FIELD_ORDER,
  RECORD_CARE_TEAM_FIELDS,
  recordFormApplies,
  browserValueWhenHidden,
  careTeamFieldOf,
  isWideField,
  isPastOnRecordForm,
  recordDialogTitle,
  recordFieldHelp,
  recordFieldLabel,
  recordPrimaryLabel,
  requiredWhen,
  selectOpening,
  tickArrangement,
  valueLabel,
} from '@/lib/recordFormWords';
import { fieldSentence, type FieldRule } from '@/lib/fieldErrorCopy';
import { errorCodeOf, fieldOf } from '@/lib/errorCopy';
import { directoryFieldByLink, directoryFieldOf } from '@/lib/recordDirectoryFields';
import { focusFirstGap } from '@/lib/formGaps';
import { practiceTodayIso } from '@/lib/practiceDate';
import { Dialog } from '@/components/primitives/Dialog';
import { DirectoryNameBox } from '@/components/clientRecord/DirectoryNameBox';
import { AppointmentWhere } from '@/components/clientRecord/AppointmentWhere';
import { holdsPlace, providerPlace, type ProviderPlace } from '@/components/clientRecord/appointmentPlace';
import { CareTeamCombobox, type CareTeamPick } from '@/components/clientRecord/CareTeamCombobox';
import { Button } from '@/components/primitives/Button';
import { Field, fieldBoxAria } from '@/components/primitives/Field';
import { SelectField } from '@/components/primitives/SelectField';
import { SegmentedToggle, type SegmentedOption } from '@/components/primitives/SegmentedToggle';
import { TickGroup } from '@/components/primitives/TickGroup';
import { TextArea, TextInput } from '@/components/primitives/TextInput';
import { textBoxProps } from '@/lib/textBoxProps';
import { nameCase as toNameCase } from '@/lib/valueCase';
import { FLD, FLD_LABEL, FLD_TEXTAREA_GROW } from '@/components/primitives/modalFormStyles';
import {
  AddressCheckChoices,
  AddressCheckPrompt,
  AddressFields,
  useAddressForm,
  type AddressFormParts,
} from '@/components/clientRecord/AddressesCard';
import { CONTACT_ROLE_KEYS, checkboxFieldLabel, contactRoleWord, type ContactRoleKey } from '@/lib/fieldLabels';
import { contactEmailProblem } from '@/lib/clientBillingContacts';
import { formatPhoneDisplay, formatPhoneInput, normalizePhone, phoneProblem } from '@/lib/phone';
import { DateInput } from '@/components/primitives/DateInput';
import { BillingHint, CheckboxRow } from '@/components/primitives/CheckboxRow';

/** String fields that are phone numbers: format-as-you-type, the
 *  sentence on leaving the box, normalized storage. */
const PHONE_FIELDS: ReadonlySet<string> = new Set(['phone', 'office_phone', 'card_phone']);

/** The one-line boxes are block-level, as the SelectField's wrapper is,
 *  so two boxes sharing a grid row start at the same height: an
 *  inline-level input takes a baseline shift against the row's line at
 *  larger text sizes. */
const BOX = `${FLD} block`;

/** The set's value when every tick is off on an edit: what an add with
 *  no ticks stores. */
const CODE_STATUS_EMPTY = ['unknown'];

/** A registry value as an input string ('' when unset). */
function toInputValue(v: unknown): string {
  if (v === undefined || v === null) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

/** True when two picks name the same thing. */
function samePick(a: CareTeamPick | null, b: CareTeamPick | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind === 'care_team' || b.kind === 'care_team') {
    return a.kind === 'care_team' && b.kind === 'care_team' && a.providerId === b.providerId;
  }
  return a.type === b.type && a.id === b.id;
}

/** A two-value enum draws the segmented toggle. */
function isToggle(fs: PayloadFieldSpec): boolean {
  return fs.kind === 'enum' && (fs.values ?? []).length === 2;
}

/** A stored or proposed address as its five parts (an object with the
 *  five keys), as opposed to its text. */
function isPostalParts(v: unknown): v is AddressFormParts {
  return v !== null && typeof v === 'object' && !Array.isArray(v) && 'line1' in (v as Record<string, unknown>);
}

/** What one save carries, read at the press. */
interface RowSaveVars {
  /** The client the row belongs to (the route's, or the caller's). */
  clientId: string;
  body: Record<string, unknown>;
  mode: 'edit' | 'create';
  projection: RecordAddProjection;
  /** Files the read card photos once the row is saved; null when none are kept. */
  filePhotos: (() => Promise<void>) | null;
  errorContext: RecordEditErrorContext;
}

/** Whether two addresses' parts are the same, part by part. */
function samePostalParts(a: AddressFormParts, b: AddressFormParts): boolean {
  return a.line1 === b.line1 && a.line2 === b.line2 && a.city === b.city && a.state === b.state && a.zip === b.zip;
}

/** The Directory entry a contact is (0227), and its name as the
 *  Directory has it. */
interface ContactLink {
  type: 'individual' | 'organization';
  id: string;
  name: string;
}

/** What a linked row's boxes fall back to: the entry's phone, email and
 *  address, as parts (a contact's) and as text (a care provider's); ''
 *  and null when the entry has none. */
interface ContactDirectoryValues {
  phone: string;
  email: string;
  parts: AddressFormParts | null;
  address: string;
}

/** The boxes a linked row's Directory entry fills, by projection, and
 *  which of the entry's values each shows (a contact's address is its
 *  parts form, apart). */
const DIRECTORY_BOXES: Readonly<Partial<Record<RecordAddProjection, Readonly<Record<string, 'phone' | 'email' | 'address'>>>>> = {
  contacts: { phone: 'phone', email: 'email' },
  // 0234: a care provider's office address is the parts form, as a
  // contact's address is, and takes the same group tag.
  providers: { office_phone: 'phone', email: 'email' },
};

function valueText(values: Record<string, unknown>, key: string): string {
  const v = values[key];
  return typeof v === 'string' ? v : '';
}

/** A stored contact's or care provider's link, from the values its row
 *  hands the dialog. */
function contactLinkFromValues(on: boolean, values: Record<string, unknown>): ContactLink | null {
  if (!on) return null;
  const name = valueText(values, 'directory_name') || valueText(values, 'name') || valueText(values, 'display_name');
  const individual = valueText(values, 'directory_individual_id');
  if (individual !== '') return { type: 'individual', id: individual, name };
  const org = valueText(values, 'directory_org_id');
  if (org !== '') return { type: 'organization', id: org, name };
  return null;
}

/** A stored linked contact's Directory values, from its row's values. */
function contactDirectoryFromValues(link: ContactLink | null, values: Record<string, unknown>): ContactDirectoryValues | null {
  if (link === null) return null;
  const part = (k: string): string | null => valueText(values, `directory_address_${k}`) || null;
  const line1 = part('line1');
  return {
    phone: valueText(values, 'directory_phone'),
    email: valueText(values, 'directory_email'),
    parts: line1 === null ? null : { line1, line2: part('line2'), city: part('city'), state: part('state'), zip: part('zip') },
    address: valueText(values, 'directory_address'),
  };
}

/** The five fields as parts (blank is null), to compare with an entry's. */
function formParts(v: { line1: string; line2: string; city: string; state: string; zip: string }): AddressFormParts {
  const n = (x: string): string | null => (x.trim() === '' ? null : x.trim());
  return { line1: n(v.line1), line2: n(v.line2), city: n(v.city), state: n(v.state), zip: n(v.zip) };
}

/** A Directory entry's address as parts, when it has a street. */
function detailParts(d: {
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
}): AddressFormParts | null {
  if (d.address_line1 === undefined || d.address_line1 === null || d.address_line1.trim() === '') return null;
  return {
    line1: d.address_line1,
    line2: d.address_line2 ?? null,
    city: d.address_city ?? null,
    state: d.address_state ?? null,
    zip: d.address_zip ?? null,
  };
}

/** The roles' order and words for a contact's tick boxes. */
function isContactRole(field: string): field is ContactRoleKey {
  return (CONTACT_ROLE_KEYS as readonly string[]).includes(field);
}

export interface RowEditDialogProps {
  /** The dialog's title; the projection's own when absent. */
  title?: string;
  /** The primary's words; the projection's own when absent. */
  primaryLabel?: string;
  /** One paragraph under the title. */
  intro?: string;
  /** A link under the intro (a linked provider's Directory page). */
  introLink?: { text: string; to: string };
  /** Greyed text an empty box shows, by registry field (never a value). */
  placeholders?: Readonly<Record<string, string>>;
  /** Extra help under a field, by registry field. */
  fieldHelp?: Readonly<Record<string, string>>;
  /** What a field travels as when the person empties it, by registry field
   *  (a linked provider's name: the Directory's, so clearing returns the
   *  row to it). Such a field is not required. */
  emptyValue?: Readonly<Record<string, string>>;
  /** Only these registry fields are drawn, in this order. Change
   *  detection, riders and sending still run over the full set. */
  fields?: readonly string[];
  /** A projection token. The ADD union is the superset (edit tokens +
   *  providers + hospital-events); edit-mode callers only ever
   *  pass an edit token (RowEditConfig). */
  projection: RecordAddProjection;
  /** The addressed row id (unused in create mode). */
  rowId: string;
  /** Edit: the current values, canonical registry keys (snake_case).
   *  Create: the values the dialog opens on (a preset), which travel
   *  drawn or not. */
  values: Record<string, unknown>;
  /** 'edit' (default) PATCHes the addressed row; 'create' POSTs the
   *  projection route. (Providers get their own add dialog.) */
  mode?: 'edit' | 'create';
  /** Fired after a successful save, before the dialog closes: the
   *  open-on-add seam (a collapsed card reveals the row just added). */
  onSaved?: () => void;
  onClose: () => void;
    /** Add insurance only: a front photo handed over from Documents ("Read
   *  into Insurance"). The photo panel opens on it and reads at once. */
  cardPhotoFront?: File;
  /** A contact's form: the client's pronouns for "Lives with"; the
   *  record the page already read when absent. */
  clientPronouns?: ClientPronouns | null;
  /** The client the row belongs to; the route's when absent (a dialog
   *  opened off the client page names it). */
  clientId?: string;
  /** Drawn above the fields (the visit-or-appointment toggle). */
  header?: ReactNode;
  /** With `fields`: these are drawn only after a quiet "More" press. They
   *  travel as any undrawn field does (defaults and presets included). */
  more?: readonly string[];
  /** Every change to what the boxes hold, as the values a reopen would
   *  take as its preset (the visit-or-appointment toggle keeps them). */
  onValuesChange?: (values: Record<string, string>) => void;
  /** Fields that sit side by side even on a phone (two small boxes);
   *  every other field then takes the full width there. */
  phonePairs?: readonly string[];
}

export function RowEditDialog({
  title,
  primaryLabel,
  intro,
  introLink,
  placeholders,
  fieldHelp,
  emptyValue,
  fields: drawnOnly,
  projection,
  rowId,
  values,
  mode = 'edit',
  onSaved,
  onClose,
  cardPhotoFront,
  clientPronouns,
  clientId: clientIdProp,
  header,
  more,
  onValuesChange,
  phonePairs,
}: RowEditDialogProps): ReactNode {
  const params = useParams<{ tenantSlug: string; clientId: string }>();
  const tenantSlug = params.tenantSlug ?? '';
  const clientId = clientIdProp ?? params.clientId ?? '';
  const [moreOpen, setMoreOpen] = useState(false);
  const queryClient = useQueryClient();
  // Only the office chooses who receives the bill: any other seat reads
  // the fact where the Billing Contact checkbox would be.
  const { whoami, tenantSlug: sessionSlug } = useTenant();
  // The error copy's contact, from the shell's cached tenant config.
  const contact = useSupportContact({ enabled: false });
  const mayChooseBillTo = canSetBillTo(whoami);
  // The insurance card door: Add insurance, a seat that keeps the record
  // and uses the AI tools, and the tenant's photo reader on.
  const cardDoor = usePhotoIntakeDoor(
    sessionSlug,
    projection === 'coverage' && mode === 'create' && canWriteClinicalRecord(whoami) && canUseAiTools(whoami),
  );
  // Values the last read put in a box, so a second read may replace them
  // while nobody has changed them.
  const [readFilled, setReadFilled] = useState<Record<string, string>>({});
  const [keepPhotos, setKeepPhotos] = useState(false);
  const [readPhotos, setReadPhotos] = useState<IntakeImage[]>([]);
  // The insurance saved but its photos were not filed: only Close remains.
  const [notFiled, setNotFiled] = useState(false);

  // The ADD and edit maps cover the same projections, so ONE lookup
  // serves both modes.
  const spec = PROPOSAL_PAYLOAD_SPECS[RECORD_ADD_PROJECTIONS[projection].specType];
  const payloadOnly: readonly string[] = RECORD_ADD_PROJECTIONS[projection].payloadOnly;
  // `contractFields` = the full edit-contract set (value logic + the
  // payload-only resubmit that preserves display-only fields like
  // date_text), minus merge-only fields (code_status_value; the direct
  // route rejects it). `directFormFields` = the subset drawn as inputs.
  const contractFields = editableFields(spec).filter(([, fs]) => !fs.mergeOnly && !fs.directoryOnly);

  const [edits, setEdits] = useState<Record<string, string>>({});
  // Reported to a caller that keeps the boxes across a remount (the
  // visit-or-appointment toggle): the preset with every edit over it.
  useEffect(() => {
    if (onValuesChange === undefined) return;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) if (typeof v === 'string') out[k] = v;
    onValuesChange({ ...out, ...edits });
  }, [edits]);
  const [error, setError] = useState<string | null>(null);
  // The sentence a gap shows under its box after a press; it clears on
  // the next change to that box.
  const [gaps, setGaps] = useState<Record<string, string>>({});
  // Per-field sentences shown after a box is left: a phone's problem
  // and an email's advisory sentence, which never blocks the primary.
  const [blurMessages, setBlurMessages] = useState<Record<string, string | null>>({});
  // The Directory links a pick set, keyed by the registry uuid field;
  // a keystroke in the box or a refused link clears one.
  const [links, setLinks] = useState<Record<string, string | null>>({});


  // The care-team field (a medication's prescriber): the client's care
  // team and pronouns come from the record the page already read, never
  // fetched. An edit opens on the row's linked care-team row as its pick.
  const careTeamField = RECORD_CARE_TEAM_FIELDS[projection];
  const cached = careTeamField === undefined ? undefined : cachedClientRecord(queryClient, tenantSlug, clientId);
  // A stay (LP-19), which keeps the Directory organization alone, opens
  // on that organization.
  const [startPick] = useState<CareTeamPick | null>(() => {
    if (mode !== 'edit' || careTeamField === undefined) return null;
    const idField = careTeamField.providerIdField;
    const linked = idField === undefined ? values[careTeamField.orgField] : values[idField];
    if (typeof linked !== 'string' || linked === '') return null;
    return idField === undefined
      ? { kind: 'directory', type: 'organization', id: linked }
      : { kind: 'care_team', providerId: linked };
  });
  const [pick, setPick] = useState<CareTeamPick | null>(startPick);

  // An appointment's place (0233): where a Directory pick came from, the
  // search Change opens, and an entry's address when it has text only.
  // A pick copies the name and parts into the boxes; only its id travels.
  const isAppointments = projection === 'appointments';
  const [placeLink, setPlaceLink] = useState<ContactLink | null>(() => {
    if (!isAppointments) return null;
    const name = valueText(values, 'location_name');
    const org = valueText(values, 'location_directory_org_id');
    if (org !== '') return { type: 'organization', id: org, name };
    const individual = valueText(values, 'location_directory_individual_id');
    if (individual !== '') return { type: 'individual', id: individual, name };
    return null;
  });
  const [startPlaceLink] = useState<ContactLink | null>(placeLink);
  const [placeSearching, setPlaceSearching] = useState(false);
  const [placeDirectoryText, setPlaceDirectoryText] = useState<string | null>(null);
  // LP-26: a care-team provider's office filled into the place when the
  // row has no Directory link (the place name Where holds), and the place
  // a provider pick offers over a place already set.
  const [placeFromTeam, setPlaceFromTeam] = useState<string | null>(null);
  const [placeOffer, setPlaceOffer] = useState<ProviderPlace | null>(null);
  const providerPickSeq = useRef(0);

  /** The place's Directory id, when a pick changed it. */
  function placeLinkFields(): Record<string, unknown> {
    if (placeLink === null) {
      // A dropped pick (Use another place, or a keystroke in the name
      // box) forgets the stored arm: an edit's explicit null.
      if (startPlaceLink === null) return {};
      return startPlaceLink.type === 'individual'
        ? { location_directory_individual_id: null }
        : { location_directory_org_id: null };
    }
    if (startPlaceLink !== null && startPlaceLink.id === placeLink.id && startPlaceLink.type === placeLink.type) return {};
    return placeLink.type === 'individual'
      ? { location_directory_individual_id: placeLink.id }
      : { location_directory_org_id: placeLink.id };
  }

  // A contact can be a Directory entry (0227): the link, the entry's
  // values its phone, email and address fall back to, and the search
  // Change opens. Only what differs from the entry travels.
  // A linked care provider's office phone and address work the same
  // way; its link changes only through Link to the Directory.
  const isContacts = projection === 'contacts';
  const isProviders = projection === 'providers';
  const directoryBoxes = DIRECTORY_BOXES[projection] ?? {};
  const hasDirectoryBox = (field: string): boolean => directoryBoxes[field] !== undefined;
  const [contactLink, setContactLink] = useState<ContactLink | null>(() =>
    contactLinkFromValues(isContacts || isProviders, values),
  );
  const [startLink] = useState<ContactLink | null>(contactLink);
  const [directoryValues, setDirectoryValues] = useState<ContactDirectoryValues | null>(() =>
    contactDirectoryFromValues(contactLink, values),
  );
  const [contactSearching, setContactSearching] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const pronouns: ClientPronouns | null =
    clientPronouns !== undefined
      ? clientPronouns
      : isContacts || isProviders
        ? (cachedClientRecord(queryClient, tenantSlug, clientId)?.vitals.pronouns ?? null)
        : null;

  /** Whether a box's value is the entry's (a phone by its digits). */
  const isDirectoryValue = (field: string, cur: string): boolean => {
    const dir = directoryValue(field).trim();
    if (dir === '' || cur.trim() === '') return false;
    return PHONE_FIELDS.has(field) ? normalizePhone(cur) === normalizePhone(dir) : cur.trim() === dir;
  };

  /** The entry's value for a box it fills ('' when none). */
  const directoryValue = (field: string): string => {
    const key = directoryBoxes[field];
    return directoryValues === null || key === undefined ? '' : directoryValues[key];
  };

  // What a box opens on: the stored value on an edit; on an add, the
  // preset, else the registry default, else nothing. A linked contact's
  // phone and email open on the entry's when the contact has none.
  const initial = (field: string): string => {
    const own =
      mode === 'edit' || values[field] !== undefined ? toInputValue(values[field]) : (spec[field]?.defaultValue ?? '');
    return own === '' ? directoryValue(field) : own;
  };
  const current = (field: string): string => edits[field] ?? initial(field);

  // A field applies to the values as they stand: the registry's rule
  // and the browser's. What does not apply is not drawn, not counted
  // and not sent. Its state stays in `edits` meanwhile, so it shows
  // again as it was when the value that hides it comes back.
  const applies = ([field, fs]: [string, PayloadFieldSpec]): boolean =>
    fieldApplies(fs, current) && recordFormApplies(projection, field, current);
  const fields = contractFields.filter(applies);
  // The fields drawn when the dialog opened, for a rule that clears a
  // stored value the edit hides (clearWhenHidden).
  const [drawnAtOpen] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        contractFields
          .filter(([field, fs]) => fieldApplies(fs, initial) && recordFormApplies(projection, field, initial))
          .map(([field]) => field),
      ),
  );
  const drawnAll = directFormFields(spec).filter(applies);
  const order = drawnOnly !== undefined ? (moreOpen && more !== undefined ? [...drawnOnly, ...more] : drawnOnly) : RECORD_FIELD_ORDER[projection];
  const drawn =
    order === undefined
      ? drawnAll
      : order
          .map((f) => drawnAll.find(([field]) => field === f))
          .filter((entry): entry is [string, PayloadFieldSpec] => entry !== undefined);

  const labelOf = (field: string): string => recordFieldLabel(projection, field);
  const isRequired = (field: string, fs: PayloadFieldSpec): boolean =>
    emptyValue?.[field] === undefined && (fs.required === true || requiredWhen(projection, field, current));

  /** One value as it travels. */
  function wireValue(field: string, fs: PayloadFieldSpec, cur: string): unknown {
    if (fs.kind === 'enum_set') {
      // The widget state serializes the set comma-joined (toInputValue
      // of an array is the same shape); the wire wants the array back.
      return cur.split(',').filter(Boolean);
    }
    if (fs.kind === 'boolean') return cur === 'true';
    if (PHONE_FIELDS.has(field) && fs.kind === 'string') {
      // Stored normalized (digits / "+digits"); display re-formats.
      return normalizePhone(cur);
    }
    // Stored lower-case, whether or not the box was left.
    if (fs.kind === 'email') return cur.toLowerCase();
    // A name-kind field travels in its name case whether or not the box
    // was left (the primary can be pressed without a blur).
    const nameKind = fs.kind === 'string' ? textBoxProps(fs.textKind).nameCase : undefined;
    if (nameKind !== undefined) return toNameCase(cur, nameKind);
    return cur;
  }

  /** The id a care-team pick adds beside its text: exactly one of the
   *  three, and only for a pick that differs from the one the dialog
   *  opened on. */
  function pickFields(field: string): Record<string, string> {
    const entry = careTeamFieldOf(projection, field);
    if (entry === undefined || pick === null || samePick(pick, startPick)) return {};
    if (pick.kind === 'care_team') {
      if (entry.providerIdField !== undefined) return { [entry.providerIdField]: pick.providerId };
      // The record keeps the organization alone (a hospital stay): the
      // care-team row sends the Directory organization it links, if any.
      const row = (cached?.providers ?? []).find((p) => p.id === pick.providerId);
      return row?.link.kind === 'org' ? { [entry.orgField]: row.link.id } : {};
    }
    if (pick.type === 'individual') {
      return entry.individualField === undefined ? {} : { [entry.individualField]: pick.id };
    }
    return { [entry.orgField]: pick.id };
  }

  /**
   * Fill the form from an insurance card read. A box takes the read value
   * when it is empty, or still holds what the last read put there; a box
   * the person typed in is kept. Each value goes through its box's own
   * transform. Says what it kept and what the program does not show.
   */
  function applyCard(card: InsuranceCardRead, photos: IntakeImage[]): CardReadOutcome {
    const next: Record<string, string> = {};
    const filled: Record<string, string> = {};
    const kept: string[] = [];
    const now = (field: string): string => next[field] ?? current(field);
    const fillable = (field: string): boolean => {
      const cur = current(field);
      return cur.trim() === '' || (readFilled[field] !== undefined && cur === readFilled[field]);
    };
    const offer = (field: string, value: string): void => {
      if (current(field) === value) return;
      if (fillable(field)) {
        next[field] = value;
        filled[field] = value;
      } else {
        kept.push(labelOf(field));
      }
    };
    if (card.program !== null && (spec.program?.values ?? []).includes(card.program)) offer('program', card.program);
    const read: Array<[string, string]> = [];
    for (const field of CARD_TEXT_FIELDS) {
      const raw = card[field];
      const fs = spec[field];
      if (raw === null || fs === undefined || fs.kind !== 'string') continue;
      const nameKind = textBoxProps(fs.textKind).nameCase;
      const value = PHONE_FIELDS.has(field)
        ? formatPhoneInput(raw)
        : nameKind !== undefined
          ? toNameCase(raw, nameKind)
          : raw;
      read.push([field, value]);
      offer(field, value);
    }
    setEdits((prev) => ({ ...prev, ...next }));
    setReadFilled(filled);
    setReadPhotos(photos);
    setError(null);
    const hidden = read
      .filter(([field]) => {
        const fs = spec[field]!;
        return !(fieldApplies(fs, now) && recordFormApplies(projection, field, now));
      })
      .map(([field]) => labelOf(field));
    return {
      kept,
      hidden,
      needsProgram: now('program').trim() === '',
      partB: card.also_part_b,
    };
  }

  /** File the photos the last read used, as insurance documents. */
  async function filePhotos(): Promise<void> {
    const sides = ['front', 'back'];
    for (const [i, photo] of readPhotos.entries()) {
      const name = `Insurance card, ${sides[i] ?? 'back'}.jpg`;
      await documentsApi.uploadDocument(tenantSlug, clientId, {
        kind: 'insurance',
        file: intakeImageFile(photo, name),
        display_name: name,
      });
    }
    await queryClient.invalidateQueries({ queryKey: clientDocumentsKey(tenantSlug, clientId) });
  }

  /** An add: every applying field that holds a value, and a Directory
   *  or care-team pick beside its name (never alone). */
  function buildAddFields(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [field, fs] of fields) {
      if (fs.kind === 'postal_address') continue;
      const cur = current(field).trim();
      if (cur === '') continue;
      // A box still showing the Directory entry's value is not this
      // contact's own: it stays out and the entry's shows through.
      if (contactLink !== null && isDirectoryValue(field, cur)) continue;
      out[field] = wireValue(field, fs, cur);
      const directory = directoryFieldOf(projection, field);
      const link = directory === undefined ? null : (links[directory.linkField] ?? null);
      if (directory !== undefined && link !== null) out[directory.linkField] = link;
      Object.assign(out, pickFields(field));
    }
    if (contactLink !== null) out[contactLink.type === 'individual' ? 'directory_individual_id' : 'directory_org_id'] = contactLink.id;
    Object.assign(out, placeLinkFields());
    return out;
  }

  /** An edit's changes: changed fields, and null (or the code-status
   *  set's empty value) for a clearable field that held a value and is
   *  now empty; then null for a stored value the edit hid. */
  function buildEditChanges(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    // A linked contact's phone and email: the box's value is the
    // contact's own unless it is the entry's, which stores nothing; only
    // a change to what is stored travels (null clears an own value).
    const ownBox = (field: string): boolean => contactLink !== null && hasDirectoryBox(field);
    for (const [field, fs] of fields) {
      if (!ownBox(field)) continue;
      const cur = current(field).trim();
      const wanted = isDirectoryValue(field, cur) ? '' : cur;
      const stored = toInputValue(values[field]).trim();
      if (wanted === stored) continue;
      out[field] = wanted === '' ? null : wireValue(field, fs, wanted);
    }
    if (
      contactLink !== null &&
      (startLink === null || contactLink.id !== startLink.id || contactLink.type !== startLink.type)
    ) {
      out[contactLink.type === 'individual' ? 'directory_individual_id' : 'directory_org_id'] = contactLink.id;
    }
    Object.assign(out, placeLinkFields());
    for (const [field, fs] of fields) {
      if (fs.kind === 'postal_address') continue;
      if (ownBox(field)) continue;
      const cur = current(field).trim();
      const was = initial(field);
      if (cur !== '') Object.assign(out, pickFields(field));
      if (cur === was) continue;
      if (cur === '') {
        if (was === '') continue;
        if (emptyValue?.[field] !== undefined) {
          out[field] = emptyValue[field];
          continue;
        }
        if (isClearable(fs)) out[field] = null;
        else if (fs.kind === 'enum_set' && tickArrangement(projection, field) === 'code_status') {
          out[field] = CODE_STATUS_EMPTY;
        }
        continue;
      }
      out[field] = wireValue(field, fs, cur);
    }
    for (const [field, fs] of contractFields) {
      if (fs.kind === 'postal_address') continue;
      if (fields.some(([f]) => f === field)) continue;
      if (!drawnAtOpen.has(field)) continue;
      const hidden = browserValueWhenHidden(projection, field);
      if (hidden === undefined || initial(field) === '') continue;
      if (hidden === null) {
        if (isClearable(fs)) out[field] = null;
      } else if (initial(field) !== hidden) {
        out[field] = hidden;
      }
    }
    return out;
  }

  /** The payload-only riders that hold a value ride along with an edit,
   *  so they survive the event re-point; an emptied one travels as
   *  its null, never as a rider. */
  function withRiders(changes: Record<string, unknown>): Record<string, unknown> {
    const out = { ...changes };
    for (const field of payloadOnly) {
      if (out[field] === undefined && current(field).trim() !== '') {
        out[field] = current(field).trim();
      }
    }
    return out;
  }

  // A refused field reads in the words this dialog shows: its label,
  // its choices' words; the values are what the boxes held when the
  // primary was pressed.
  function errorContext(): RecordEditErrorContext {
    const sent: Record<string, unknown> = {};
    const labels: Record<string, string> = {};
    const valueLabels: Record<string, Record<string, string>> = {};
    for (const [field, fs] of fields) {
      if (fs.kind === 'postal_address') continue;
      sent[field] = current(field).trim();
      labels[field] = fs.kind === 'boolean' ? checkboxFieldLabel(field) : labelOf(field);
      if (fs.kind === 'enum' || fs.kind === 'enum_set') {
        valueLabels[field] = Object.fromEntries((fs.values ?? []).map((v) => [v, valueLabel(projection, field, v)]));
      }
    }
    return { specs: spec, sent, labels, valueLabels };
  }

  // Everything the save and its callbacks need is read at the press and
  // travels as the variables (saveVars): the body, the mode and projection,
  // whether to file the card photos and how, and the error's context. The
  // option functions read nothing from this render.
  const saveMut = useMutation<unknown, unknown, RowSaveVars>({
    mutationFn: (v) => {
      if (v.mode === 'create') {
        return postRecordRow(tenantSlug, v.clientId, v.projection, v.body);
      }
      // Edit mode only ever mounts with an edit-map token (RowEditConfig
      // types it; the add-only projections render no row affordances).
      return patchRecordRow(tenantSlug, v.clientId, v.projection as RecordEditProjection, rowId, {
        action: 'edit',
        fields: v.body,
      });
    },
    onSuccess: async (_res, v) => {
      await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
      onSaved?.();
      // The card photos are kept only once the insurance is saved; opened
      // from Documents the photo is already filed and the tick is not drawn.
      if (v.filePhotos !== null) {
        try {
          await v.filePhotos();
        } catch {
          setNotFiled(true);
          return;
        }
      }
      onClose();
    },
    onError: (err, v) => {
      // The server refused a Directory link (the entry is gone): the
      // dialog says so and drops the link, so the next press saves the
      // name as typed.
      const linkField = errorCodeOf(err) === 'invalid_value' ? fieldOf(err) : null;
      const refused = linkField === null ? undefined : directoryFieldByLink(v.projection, linkField);
      if (refused !== undefined) {
        setLinks((prev) => ({ ...prev, [refused.linkField]: null }));
        // A pick from the care-team box that named it is dropped too, so
        // the next press saves the name as typed.
        setPick(null);
        setError(refused.refused);
        return;
      }
      setError(recordEditErrorMessage(err, v.errorContext, contact));
    },
  });

  /** The save's variables, read from the screen at the press. */
  function saveVars(body: Record<string, unknown>): RowSaveVars {
    return {
      clientId,
      body,
      mode,
      projection,
      filePhotos: keepPhotos && cardPhotoFront === undefined && readPhotos.length > 0 ? filePhotos : null,
      errorContext: errorContext(),
    };
  }

  // A field of the postal_address kind (a contact's address, 0226) is the
  // shared address form, outside the boxes' string state: it opens on the
  // stored parts, shows a text-only address as on file with the fields on
  // the parts it suggests, and checks a changed street at the primary
  // before the save goes.
  const postalField = Object.entries(spec).find(([, fs]) => fs.kind === 'postal_address')?.[0];
  const storedPostal = postalField === undefined ? undefined : values[postalField];
  const storedParts = isPostalParts(storedPostal) && storedPostal.line1 !== null ? storedPostal : null;
  const storedText = typeof storedPostal === 'string' && storedPostal.trim() !== '' ? storedPostal : null;
  // A linked contact with no address of its own opens on the entry's.
  const [openParts] = useState<AddressFormParts | null>(() =>
    storedParts ?? (storedText === null ? (directoryValues?.parts ?? null) : null),
  );
  const addressForm = useAddressForm({
    existing: openParts,
    existingText: storedText,
    rule: 'street',
    // The fields' own prefix, so a gap's focus finds them.
    idPrefix: isProviders ? 'provider-office-address' : isAppointments ? 'appointment-location' : 'row-edit-address',
    enabled: postalField !== undefined,
    done: false,
    saving: saveMut.isPending,
    onSave: (parts) => send(parts),
  });

  /** The sentence for one gap, by the box's kind. */
  function gapSentence(field: string, fs: PayloadFieldSpec, cur: string): string | null {
    const label = labelOf(field);
    if (fs.kind === 'boolean') return null;
    if (isToggle(fs)) {
      if (fs.required !== true || cur !== '') return null;
      const rule: FieldRule = { kind: 'one_of', values: (fs.values ?? []).map((v) => valueLabel(projection, field, v)) };
      return fieldSentence(label, rule);
    }
    if (isRequired(field, fs) && cur === '') {
      const rule: FieldRule =
        fs.kind === 'enum' || fs.kind === 'risk_category'
          ? { kind: 'from_list' }
          : fs.kind === 'date'
            ? { kind: 'date' }
            : fs.kind === 'enum_set'
              ? { kind: 'some_of', values: (fs.values ?? []).map((v) => valueLabel(projection, field, v)) }
              : { kind: 'text' };
      return fieldSentence(label, rule, cur);
    }
    if (PHONE_FIELDS.has(field) && fs.kind === 'string' && cur !== initial(field)) {
      return phoneProblem(cur);
    }
    if (fs.kind === 'date' && cur !== '' && isPastOnRecordForm(projection, field)) {
      const today = practiceTodayIso();
      if (cur > today) return fieldSentence(label, { kind: 'not_future', today }, cur);
    }
    return null;
  }

  /** The id a gap's focus lands on: the box, or a toggle's first radio. */
  function gapFocusId(field: string, fs: PayloadFieldSpec): string {
    const id = `row-edit-${field}`;
    return isToggle(fs) ? `${id}-${fs.values![0]}` : id;
  }

  function onPrimary(): void {
    const found: Array<[string, string, string]> = [];
    for (const [field, fs] of drawn) {
      if (fs.kind === 'postal_address') continue;
      const sentence = gapSentence(field, fs, current(field).trim());
      if (sentence !== null) found.push([field, sentence, gapFocusId(field, fs)]);
    }
    if (found.length > 0) {
      setGaps(Object.fromEntries(found.map(([field, sentence]) => [field, sentence])));
      focusFirstGap(found.map(([, , id]) => id));
      return;
    }
    // A linked contact's address still showing the entry's is not its
    // own: it is neither checked nor stored.
    if (postalField !== undefined && addressFromDirectory()) {
      send('directory');
      return;
    }
    // The address: empty fields send none (a stored address is cleared,
    // a text-only one is kept); typed ones go through the address form,
    // which names its own gaps and checks a changed street first.
    if (postalField !== undefined && !addressForm.isEmpty()) {
      addressForm.pressSave();
      return;
    }
    send(storedParts !== null ? null : undefined);
  }

  /** Whether the five fields show the linked entry's address as it is. */
  function addressFromDirectory(): boolean {
    const dir = directoryValues?.parts ?? null;
    return contactLink !== null && dir !== null && samePostalParts(formParts(addressForm.fields.values), dir);
  }

  /** The save, with the address as parts, null to clear it, undefined to
   *  leave it as stored, or 'directory' for a linked contact showing its
   *  entry's address (the contact keeps none of its own). */
  function send(address: AddressFormParts | null | undefined | 'directory'): void {
    if (mode === 'create') {
      const body = buildAddFields();
      if (postalField !== undefined && address !== undefined && address !== null && address !== 'directory') {
        body[postalField] = address;
      }
      saveMut.mutate(saveVars(body));
      return;
    }
    const changes = buildEditChanges();
    if (postalField !== undefined && address !== undefined) {
      if (address === 'directory') {
        if (storedParts !== null || storedText !== null) changes[postalField] = null;
      } else {
        const same =
          address === null ? storedParts === null && storedText === null : storedParts !== null && samePostalParts(storedParts, address);
        if (!same) changes[postalField] = address;
      }
    }
    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }
    saveMut.mutate(saveVars(withRiders(changes)));
  }

  /** A Directory pick for an appointment's place: the name and the
   *  entry's parts are copied into the boxes, editable; an entry with
   *  text only fills the name and shows its text over empty boxes. */
  async function pickPlace(entry: { id: string; name: string; type: 'organization' | 'individual' }): Promise<void> {
    setPlaceLink({ type: entry.type, id: entry.id, name: entry.name });
    setPlaceFromTeam(null);
    setPlaceSearching(false);
    setError(null);
    setEdits((prev) => ({ ...prev, location_name: entry.name }));
    let parts: AddressFormParts | null = null;
    let text: string | null = null;
    try {
      const d = entry.type === 'individual' ? await api.getIndividual(tenantSlug, entry.id) : await api.getProvider(tenantSlug, entry.id);
      parts = detailParts(d);
      text = typeof d.address === 'string' && d.address.trim() !== '' ? d.address : null;
    } catch {
      // The pick still records where the place came from; the boxes keep
      // what they hold.
      return;
    }
    if (parts !== null) {
      setPlaceDirectoryText(null);
      addressForm.reset(parts);
    } else {
      setPlaceDirectoryText(text);
      addressForm.reset({ line1: null, line2: null, city: null, state: null, zip: null });
    }
  }

  /** Use another place: the pick is dropped; the name and the boxes keep
   *  what they hold. */
  function clearPlace(): void {
    setPlaceLink(null);
    setPlaceFromTeam(null);
    setPlaceSearching(false);
    setPlaceDirectoryText(null);
  }

  /** True while the place is empty: no pick, no name, no address part.
   *  A provider pick fills the place only then. */
  function placeIsEmpty(): boolean {
    return placeLink === null && placeFromTeam === null && current('location_name').trim() === '' && addressForm.isEmpty();
  }
  // The provider's place is fetched, so the emptiness it fills on is read
  // when the answer lands, from the form as it then stands.
  const placeIsEmptyNow = useRef(placeIsEmpty);
  placeIsEmptyNow.current = placeIsEmpty;

  /** Fills the place from a provider's place, as the confirm card does:
   *  its name and parts, and the Directory entry it links. */
  function takePlace(place: ProviderPlace): void {
    setEdits((prev) => ({ ...prev, location_name: place.name }));
    setError(null);
    if (place.parts !== null) {
      setPlaceDirectoryText(null);
      const nullable = (v: string): string | null => (v.trim() === '' ? null : v);
      addressForm.reset({
        line1: place.parts.line1,
        line2: nullable(place.parts.line2),
        city: nullable(place.parts.city),
        state: nullable(place.parts.state),
        zip: nullable(place.parts.zip),
      });
    } else {
      setPlaceDirectoryText(place.text);
      addressForm.reset({ line1: null, line2: null, city: null, state: null, zip: null });
    }
    if (place.entry !== null) {
      setPlaceLink({ type: place.entry.type, id: place.entry.id, name: place.name });
      setPlaceFromTeam(null);
    } else {
      setPlaceLink(null);
      setPlaceFromTeam(place.name);
    }
    setPlaceSearching(false);
    setPlaceOffer(null);
  }

  /** A provider pick: its place (appointmentPlace) fills an empty place,
   *  or is offered over one already set. A care-team row with neither an
   *  address nor a Directory link, or a typed provider, offers nothing. */
  async function fillPlaceFromProvider(next: { value: string; pick: CareTeamPick | null }): Promise<void> {
    if (!isAppointments) return;
    const seq = providerPickSeq.current + 1;
    providerPickSeq.current = seq;
    const place = next.pick === null ? null : await providerPlace(tenantSlug, next.pick, next.value, cached?.providers ?? []);
    if (providerPickSeq.current !== seq) return;
    if (place !== null && placeIsEmptyNow.current()) {
      takePlace(place);
      return;
    }
    setPlaceOffer(place);
  }

  /** A Directory pick for a contact: the link, the name when Name is
   *  empty, and the entry's phone, email and address, which fill every
   *  box not typed for this client. */
  async function pickContactEntry(entry: { id: string; name: string; type: 'organization' | 'individual' }): Promise<void> {
    const before = directoryValues;
    setContactLink({ type: entry.type, id: entry.id, name: entry.name });
    setContactSearching(false);
    setContactSearch('');
    setError(null);
    if (current('name').trim() === '') setEdits((prev) => ({ ...prev, name: entry.name }));
    let next: ContactDirectoryValues;
    try {
      if (entry.type === 'individual') {
        const d = await api.getIndividual(tenantSlug, entry.id);
        next = { phone: d.business_phone ?? '', email: d.email ?? '', parts: detailParts(d), address: '' };
      } else {
        const d = await api.getProvider(tenantSlug, entry.id);
        next = { phone: d.phone ?? '', email: d.email ?? '', parts: detailParts(d), address: '' };
      }
    } catch {
      // The link still saves; the boxes keep what they hold.
      setDirectoryValues({ phone: '', email: '', parts: null, address: '' });
      return;
    }
    // A box that showed the previous entry's value takes the new one.
    setEdits((prev) => {
      const out = { ...prev };
      for (const [field, key] of Object.entries(directoryBoxes)) {
        const was = before === null ? '' : before[key];
        if (out[field] !== undefined && out[field] === was) delete out[field];
      }
      return out;
    });
    setDirectoryValues(next);
    const shownBefore = before?.parts ?? null;
    if (
      next.parts !== null &&
      (addressForm.isEmpty() || (shownBefore !== null && samePostalParts(formParts(addressForm.fields.values), shownBefore)))
    ) {
      addressForm.reset(next.parts);
    }
  }

  /** Where a linked contact's box value comes from: the entry, this
   *  client, or neither (unlinked, or empty with nothing in the entry). */
  function boxSource(field: string): 'directory' | 'own' | null {
    if (contactLink === null || directoryValues === null) return null;
    const cur = current(field).trim();
    if (isDirectoryValue(field, cur)) return 'directory';
    return cur !== '' ? 'own' : null;
  }

  function addressSource(): 'directory' | 'own' | null {
    if (contactLink === null || directoryValues === null) return null;
    if (addressFromDirectory()) return 'directory';
    return !addressForm.isEmpty() || (storedParts === null && storedText !== null) ? 'own' : null;
  }

  /** The line under a linked contact's box: "From the Directory", or
   *  "Just for this client" with the way back when the entry has one. */
  const sourcePrefix = isProviders ? 'provider-field' : 'contact-field';
  function sourceLine(key: string, source: 'directory' | 'own' | null, canUse: boolean, onUse: () => void): ReactNode {
    if (source === null) return null;
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ink-soft">
        <span data-testid={`${sourcePrefix}-${key}-source`}>
          {source === 'directory' ? 'From the Directory' : 'Just for this client'}
        </span>
        {source === 'own' && canUse && (
          <Button variant="ghost" size="sm" data-testid={`${sourcePrefix}-${key}-use-directory`} onClick={onUse}>
            {"Use the Directory's"}
          </Button>
        )}
      </div>
    );
  }

  function renderInput(field: string, fs: PayloadFieldSpec): ReactNode {
    const id = `row-edit-${field}`;
    const testId = isAppointments && field === 'location_name' ? 'appointment-location-name' : `edit-field-${field}`;
    const help = recordFieldHelp(projection, field);
    const shownError = gaps[field] ?? blurMessages[field] ?? undefined;
    const aria = fieldBoxAria(id, {
      required: isRequired(field, fs),
      help: help !== undefined,
      error: shownError !== undefined,
    });
    const set = (val: string): void => {
      setError(null);
      setGaps((prev) => {
        if (prev[field] === undefined) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
      // LP-41: a medication made Active again sheds a stop date on or
      // before today (it would read as stopped at once); the box shows it
      // empty, and a later stop date stays.
      const staleStop =
        projection === 'medications' &&
        field === 'status' &&
        val === 'active' &&
        current('ended_on') !== '' &&
        current('ended_on') <= practiceTodayIso();
      setEdits((prev) => ({ ...prev, [field]: val, ...(staleStop ? { ended_on: '' } : {}) }));
    };

    if (isToggle(fs)) {
      const [a, b] = fs.values as [string, string];
      const options: readonly [SegmentedOption<string>, SegmentedOption<string>] = [
        { value: a, label: valueLabel(projection, field, a), suffix: a },
        { value: b, label: valueLabel(projection, field, b), suffix: b },
      ];
      return (
        <SegmentedToggle
          options={options}
          value={current(field) === '' ? null : current(field)}
          onChange={set}
          name={labelOf(field)}
          labelledBy={`${id}-label`}
          describedBy={aria['aria-describedby']}
          invalid={aria['aria-invalid'] === 'true'}
          required={aria['aria-required'] === 'true'}
          testIdPrefix={testId}
          id={id}
        />
      );
    }

    if (fs.kind === 'enum' || fs.kind === 'risk_category') {
      const cur = current(field);
      const opening = selectOpening(projection, field);
      // The risk list is the platform vocabulary plus the current value;
      // the server validates against the tenant's own list. A registry
      // default is listed first.
      const listed =
        fs.kind === 'risk_category'
          ? cur && !DEFAULT_RISK_CATEGORIES.includes(cur)
            ? [...DEFAULT_RISK_CATEGORIES, cur]
            : DEFAULT_RISK_CATEGORIES
          : (fs.values ?? []);
      const options =
        fs.defaultValue !== undefined && listed.includes(fs.defaultValue)
          ? [fs.defaultValue, ...listed.filter((v) => v !== fs.defaultValue)]
          : listed;
      return (
        <SelectField size="field" id={id} data-testid={testId} value={cur} onChange={(e) => set(e.target.value)} {...aria}>
          {(opening !== undefined || fs.defaultValue === undefined) && (
            <option value="">{opening ?? `Choose ${labelOf(field)}`}</option>
          )}
          {options.map((v) => (
            <option key={v} value={v}>
              {valueLabel(projection, field, v)}
            </option>
          ))}
        </SelectField>
      );
    }

    if (fs.kind === 'enum_set' && tickArrangement(projection, field) === 'ticks') {
      // One wrapping row of ticks; the state is the comma-joined set in
      // vocabulary order, which is how it travels.
      return (
        <TickGroup
          id={id}
          legend={labelOf(field)}
          options={((fs.values ?? []) as readonly string[]).map((v) => ({
            value: v,
            label: valueLabel(projection, field, v),
          }))}
          value={current(field).split(',').filter(Boolean)}
          onChange={(next) => set(next.join(','))}
          testIdPrefix={testId}
          describedBy={aria['aria-describedby']}
        />
      );
    }

    if (fs.kind === 'enum_set') {
      // The code-status ticks: DNR/DNI/DNH combine; Full code, Comfort
      // care and Not recorded stand alone. The widget applies the same
      // rule the writer's merge does: picking one that stands alone
      // clears the ticks; ticking one evicts the single choice. State
      // serializes as a comma-joined set in vocabulary order.
      const vocabulary = (fs.values ?? []) as readonly string[];
      const selected = new Set(current(field).split(',').filter(Boolean));
      const combinable = vocabulary.filter((v) => CODE_STATUS_COMBINABLE.includes(v));
      const singletons = vocabulary.filter((v) => !CODE_STATUS_COMBINABLE.includes(v));
      const serialize = (next: Set<string>): string => vocabulary.filter((v) => next.has(v)).join(',');
      const toggle = (v: string): void => {
        const next = new Set([...selected].filter((m) => CODE_STATUS_COMBINABLE.includes(m)));
        if (selected.has(v)) next.delete(v);
        else next.add(v);
        set(serialize(next));
      };
      const pick = (v: string): void => set(v);
      return (
        <div className="flex flex-col gap-1.5" data-testid={testId} role="group" aria-labelledby={`${id}-label`} {...aria}>
          <div className="flex flex-wrap gap-3">
            {combinable.map((v) => (
              <label key={v} className="inline-flex cursor-pointer items-center gap-2 text-[15px] text-sage-800">
                <input
                  type="checkbox"
                  data-testid={`${testId}-${v}`}
                  className="h-5 w-5 accent-sage-700"
                  checked={selected.has(v)}
                  onChange={() => toggle(v)}
                />
                {valueLabel(projection, field, v)}
              </label>
            ))}
          </div>
          <p data-testid={`${testId}-group-line`} className="text-xs text-ink-soft">
            {CODE_STATUS_GROUP_LINE}
          </p>
          <div className="flex flex-wrap gap-3">
            {singletons.map((v) => (
              <label key={v} className="inline-flex cursor-pointer items-center gap-2 text-[15px] text-sage-800">
                <input
                  type="radio"
                  name={id}
                  data-testid={`${testId}-${v}`}
                  className="h-5 w-5 accent-sage-700"
                  checked={selected.size === 1 && selected.has(v)}
                  onChange={() => pick(v)}
                />
                {valueLabel(projection, field, v)}
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (fs.kind === 'boolean') {
      // The office sets who receives the bill: a seat without set_bill_to
      // reads the fact where the checkbox would be and never sends the flag.
      // A contact's roles read in the one map's words, "Lives with"
      // naming the client by pronoun.
      const word = isContacts && isContactRole(field) ? contactRoleWord(field, pronouns) : checkboxFieldLabel(field);
      return (
        <CheckboxRow
          id={id}
          testId={testId}
          label={word}
          checked={current(field) === 'true'}
          onChange={(checked) => set(checked ? 'true' : 'false')}
          billing={field === 'is_billing'}
          mayChooseBillTo={mayChooseBillTo}
        />
      );
    }

    // A box of many lines that grows with its text: a typed address, a
    // clinical note.
    if (fs.kind === 'string' && fs.multiline === true) {
      return (
        <TextArea
          id={id}
          data-testid={testId}
          grow
          rows={3}
          className={FLD_TEXTAREA_GROW}
          value={current(field)}
          placeholder={placeholders?.[field]}
          onChange={(e) => set(e.target.value)}
          {...textBoxProps(fs.textKind)}
          {...aria}
        />
      );
    }

    // Phone: formats as you type (a leading + goes international), shows
    // its sentence on leaving the box and on a press, and travels
    // normalized.
    if (fs.kind === 'string' && PHONE_FIELDS.has(field)) {
      const shown = edits[field] !== undefined ? edits[field] : formatPhoneDisplay(initial(field));
      return (
        <TextInput
          id={id}
          data-testid={testId}
          type="tel"
          className={BOX}
          value={shown}
          placeholder={placeholders?.[field]}
          onChange={(e) => {
            setBlurMessages((prev) => ({ ...prev, [field]: null }));
            set(formatPhoneInput(e.target.value));
          }}
          onBlur={() => setBlurMessages((prev) => ({ ...prev, [field]: phoneProblem(current(field)) }))}
          {...aria}
        />
      );
    }

    // Email: an email box, never capitalised. Leaving a box the person
    // typed in trims and lower-cases it; an untouched box keeps the
    // stored value, so tabbing through never makes an edit. A changed
    // value with a shape problem shows its sentence after leaving; it is
    // advisory, so the primary sends the text lower-cased. Typing again
    // clears it.
    if (fs.kind === 'email') {
      return (
        <TextInput
          id={id}
          data-testid={testId}
          type="email"
          spellCheck={false}
          autoCapitalize="none"
          autoComplete="off"
          className={BOX}
          value={current(field)}
          onChange={(e) => {
            setBlurMessages((prev) => ({ ...prev, [field]: null }));
            set(e.target.value);
          }}
          onBlur={() => {
            const typed = edits[field];
            if (typed === undefined) return;
            const lowered = typed.trim().toLowerCase();
            set(lowered);
            setBlurMessages((prev) => ({
              ...prev,
              [field]: lowered === initial(field) ? null : contactEmailProblem(lowered),
            }));
          }}
          {...aria}
        />
      );
    }

    // The care-team combobox: its text is the value; a pick names a
    // care-team row or a Directory entry, and a keystroke drops it.
    const careTeam = fs.kind === 'string' ? careTeamFieldOf(projection, field) : undefined;
    if (careTeam !== undefined) {
      return (
        <CareTeamCombobox
          id={id}
          value={current(field)}
          pick={pick}
          onChange={(next) => {
            setPick(next.pick);
            set(next.value);
            if (field === 'provider') void fillPlaceFromProvider(next);
          }}
          careTeam={cached?.providers ?? []}
          pronouns={cached?.vitals.pronouns ?? null}
          listLabel={careTeam.listLabel}
          only={careTeam.only}
          nameKind={careTeam.nameKind}
          testIdPrefix={testId}
          describedBy={aria['aria-describedby']}
          invalid={aria['aria-invalid'] === 'true'}
          required={aria['aria-required'] === 'true'}
        />
      );
    }

    // A box that searches the Directory on an add: its text is the value;
    // a pick adds the link, a keystroke drops it. An edit draws the plain
    // box (a stay's facility, the one such field with an edit, draws the
    // care-team combobox above first).
    const directory = mode === 'create' && fs.kind === 'string' ? directoryFieldOf(projection, field) : undefined;
    if (directory !== undefined) {
      return (
        <DirectoryNameBox
          id={id}
          testId={testId}
          value={current(field)}
          onChange={(next) => {
            setLinks((prev) => ({ ...prev, [directory.linkField]: null }));
            set(next);
          }}
          onPick={(entry) => {
            set(entry.name);
            setLinks((prev) => ({ ...prev, [directory.linkField]: entry.id }));
          }}
          only={directory.only}
          listLabel={directory.listLabel}
          linked={(links[directory.linkField] ?? null) !== null}
          boxAria={aria}
        />
      );
    }

    // A date or a string. A name kind takes its name case on leaving the
    // box; every other value is kept as typed and reads with its capital
    // where it is shown. A date past by nature caps at the practice's
    // today.
    if (fs.kind === 'date') {
      return (
        <DateInput
          id={id}
          data-testid={testId}
          size="field"
          className="block w-full max-w-[11rem]"
          max={isPastOnRecordForm(projection, field) ? practiceTodayIso() : undefined}
          value={current(field)}
          onChange={(e) => set(e.target.value)}
          {...aria}
        />
      );
    }
    return (
      <TextInput
        id={id}
        data-testid={testId}
        type={fs.kind === 'time' ? 'time' : 'text'}
        className={BOX}
        value={current(field)}
        placeholder={placeholders?.[field]}
        onChange={(e) => set(e.target.value)}
        {...(fs.kind === 'string' ? textBoxProps(fs.textKind) : {})}
        {...aria}
      />
    );
  }

  return (
    <Dialog
      title={title ?? recordDialogTitle(projection, mode)}
      size="xl"
      testId="row-edit-dialog"
      onClose={onClose}
      footer={
        notFiled ? (
          <Button variant="primary" size="sm" data-testid="row-edit-close" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            {addressForm.prompting ? (
              <AddressCheckChoices
                phase={addressForm.check.phase}
                busy={saveMut.isPending}
                onKeep={() => addressForm.choose('checked', false)}
                onUse={() => addressForm.choose('checked', true)}
                onSaveAsTyped={() => addressForm.choose('saved_as_typed', false)}
              />
            ) : (
              <Button
                variant="primary"
                size="sm"
                disabled={saveMut.isPending || addressForm.checking}
                loading={saveMut.isPending || addressForm.checking}
                data-testid="row-edit-save"
                onClick={onPrimary}
              >
                {primaryLabel ?? recordPrimaryLabel(projection, mode)}
              </Button>
            )}
          </>
        )
      }
    >
      {header}
      {intro !== undefined && (
        <p data-testid="row-edit-intro" className="text-sm text-ink-soft mb-2">
          {intro}
          {introLink !== undefined && (
            <>
              {' '}
              <Link to={introLink.to} data-testid="row-edit-intro-link" className="font-semibold text-sage-800 underline">
                {introLink.text}
              </Link>
            </>
          )}
        </p>
      )}
      {cardDoor && !notFiled && (
        <CoverageCardPhoto
          startFront={cardPhotoFront}
          onRead={applyCard}
          keep={keepPhotos}
          onKeepChange={setKeepPhotos}
        />
      )}
      {notFiled && (
        <ErrorNotice surface="insurance_card_extract" code="not_filed" data-testid="coverage-photo-not-filed" />
      )}
      {isContacts &&
        (contactLink !== null && !contactSearching ? (
          <div data-testid="contact-directory-picked" className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-2 text-sm text-sage-800">
            <span className="text-xs text-ink-soft">{contactLink.type === 'individual' ? 'Person' : 'Organization'}</span>
            <span>
              From the Directory: <span className="font-semibold">{contactLink.name}</span>
            </span>
            <Button variant="ghost" size="sm" data-testid="contact-directory-change" onClick={() => setContactSearching(true)}>
              Change
            </Button>
          </div>
        ) : (
          <div className="pt-2">
            <Field
              label="Find in the Directory"
              help="For a professional, such as an attorney or a guardian: picking one brings their phone, email and address."
              htmlFor="row-edit-contact-directory"
            >
              <DirectoryNameBox
                id="row-edit-contact-directory"
                testId="contact-directory-search"
                value={contactSearch}
                onChange={setContactSearch}
                onPick={(entry) => void pickContactEntry(entry)}
                listLabel="Directory entries"
                linked={false}
                boxAria={fieldBoxAria('row-edit-contact-directory', { required: false, help: true, error: false })}
              />
            </Field>
          </div>
        ))}
      {/* Two columns from `sm`, one below. A box of many lines, a set of
          ticks, a field named in RECORD_WIDE_FIELDS and the flag group
          span both. A row of ticks carries its own legend. */}
      <div className={`grid gap-x-4 gap-y-3 py-2 ${phonePairs !== undefined ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`}>
        {drawn
          .filter(([, fs]) => fs.kind !== 'boolean')
          .map(([field, fs]) => (
            <Fragment key={field}>
            {isAppointments && field === 'location_name' && (
              // One Where block (the confirm card draws the same): the
              // name line, a Directory name box, and the address boxes.
              <div className={phonePairs !== undefined ? 'col-span-2' : 'sm:col-span-2'}>
                <AppointmentWhere
                  surface="form"
                  nameId="row-edit-location_name"
                  nameTestId="appointment-location-name"
                  addressIdPrefix="appointment-location"
                  addressTestId="edit-field-location_address"
                  name={current('location_name')}
                  onNameChange={(v) => {
                    setEdits((prev) => ({ ...prev, location_name: v }));
                    setError(null);
                    if (placeLink !== null || placeFromTeam !== null) clearPlace();
                  }}
                  onPick={(entry) => void pickPlace(entry)}
                  link={
                    placeLink !== null
                      ? { name: placeLink.name }
                      : placeFromTeam !== null
                        ? { name: placeFromTeam, from: 'care_team' }
                        : null
                  }
                  offer={
                    placeOffer !== null && !holdsPlace({ name: current('location_name'), entry: placeLink }, placeOffer)
                      ? { name: placeOffer.name, onUse: () => takePlace(placeOffer) }
                      : null
                  }
                  searching={placeSearching}
                  onChange={() => setPlaceSearching(true)}
                  onClear={clearPlace}
                  directoryText={placeDirectoryText}
                  above={
                    storedParts === null && storedText !== null ? (
                      <p data-testid="address-on-file-text" className="text-xs text-sage-800">
                        On file as: {storedText}
                      </p>
                    ) : undefined
                  }
                  address={addressForm.fields}
                >
                  <AddressCheckPrompt phase={addressForm.check.phase} />
                </AppointmentWhere>
              </div>
            )}
            {!(isAppointments && (field === 'location_name' || field === 'location_address')) && (
            <div
              className={
                fs.multiline === true || fs.kind === 'enum_set' || fs.kind === 'postal_address' || isWideField(projection, field)
                  ? phonePairs !== undefined
                    ? 'col-span-2'
                    : 'sm:col-span-2'
                  : phonePairs === undefined || phonePairs.includes(field)
                    ? undefined
                    : 'col-span-2 sm:col-span-1'
              }
            >
              {fs.kind === 'postal_address' ? (
                <fieldset className="flex min-w-0 flex-col gap-2">
                  <legend className={FLD_LABEL}>{labelOf(field)}</legend>
                  {storedParts === null && storedText !== null && (
                    <p data-testid="address-on-file-text" className="text-xs text-sage-800">
                      On file as: {storedText}
                    </p>
                  )}
                  <AddressFields
                    idPrefix={isProviders ? 'provider-office-address' : isAppointments ? 'appointment-location' : 'row-edit-address'}
                    {...addressForm.fields}
                  />
                  {(isContacts || isProviders) &&
                    sourceLine(isProviders ? 'office_address' : 'address', addressSource(), (directoryValues?.parts ?? null) !== null, () => {
                      if (directoryValues?.parts) addressForm.reset(directoryValues.parts);
                      addressForm.touched();
                    })}
                  <AddressCheckPrompt phase={addressForm.check.phase} />
                </fieldset>
              ) : fs.kind === 'enum_set' && tickArrangement(projection, field) === 'ticks' ? (
                renderInput(field, fs)
              ) : (
                <Field
                  label={labelOf(field)}
                  required={isRequired(field, fs)}
                  help={fieldHelp?.[field] ?? recordFieldHelp(projection, field)}
                  error={gaps[field] ?? blurMessages[field] ?? undefined}
                  errorTestId={
                    PHONE_FIELDS.has(field) || fs.kind === 'email' ? `edit-field-${field}-problem` : undefined
                  }
                  group={isToggle(fs)}
                  htmlFor={`row-edit-${field}`}
                >
                  {renderInput(field, fs)}
                  {hasDirectoryBox(field) &&
                    sourceLine(field, boxSource(field), directoryValue(field).trim() !== '', () =>
                      // The entry's value as the box shows it (a phone in
                      // the house format).
                      setEdits((prev) => ({
                        ...prev,
                        [field]: PHONE_FIELDS.has(field) ? formatPhoneDisplay(directoryValue(field)) : directoryValue(field),
                      })),
                    )}
                </Field>
              )}
            </div>
            )}
            </Fragment>
          ))}
        {isContacts && drawn.some(([, fs]) => fs.kind === 'boolean') && (
          // A contact's roles, in the one map's order (CONTACT_ROLE_KEYS).
          <fieldset data-testid="row-edit-flags" className="flex min-w-0 flex-col gap-1 sm:col-span-2">
            <legend className={FLD_LABEL}>Their roles</legend>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
              {CONTACT_ROLE_KEYS.map((field) => drawn.find(([f]) => f === field))
                .filter((entry): entry is [string, PayloadFieldSpec] => entry !== undefined)
                .map(([field, fs]) => (
                  <span key={field}>{renderInput(field, fs)}</span>
                ))}
            </div>
            {/* Below the whole group, only while Billing contact is ticked. */}
            {mayChooseBillTo && current('is_billing') === 'true' && <BillingHint />}
          </fieldset>
        )}
        {!isContacts && drawn.some(([, fs]) => fs.kind === 'boolean') && (
          <div data-testid="row-edit-flags" className="flex flex-wrap items-center gap-x-6 gap-y-1 sm:col-span-2">
            {drawn
              .filter(([, fs]) => fs.kind === 'boolean')
              .map(([field, fs]) => (
                <span key={field}>{renderInput(field, fs)}</span>
              ))}
          </div>
        )}
      </div>
      {more !== undefined && more.length > 0 && !moreOpen && (
        <div className="mb-2">
          <TextAction className="-ml-2" data-testid="row-edit-more" aria-expanded={false} onClick={() => setMoreOpen(true)}>
            More
          </TextAction>
        </div>
      )}
      {error !== null && (
        <p role="alert" data-testid="row-edit-error" className="text-xs text-pepper-deep">
          {error}
        </p>
      )}
    </Dialog>
  );
}
